import { BookOutlined, CheckOutlined, CopyOutlined, DeleteFilled, DiffOutlined, DownloadOutlined, DownOutlined, EditOutlined, EnvironmentOutlined, HomeOutlined, InfoCircleOutlined, LinkOutlined, PaperClipOutlined, PlusSquareOutlined, QuestionCircleOutlined, SmileOutlined, TagOutlined, TruckOutlined, ShoppingOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { Button, DatePicker, Dropdown, Form, Input, InputNumber, Modal, Popover, Select, Switch, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Picker from 'emoji-picker-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useClickOutSide from '../../../helpers/useClickOutSide';
import { showErrorMessage, showLoader, showSuccessMessage } from '../../../utils';
import { postData } from '../../../services/HomeService';
import { uploadFile } from '../../../services/UploadService';
import { BASE_URL, SUPER_CATEGORY_LIST, getSubCategoriesForSuperCategory } from '../../../constants';
import { useSelector } from 'react-redux';
import {
  fetchQuoteWithQuoteId,
  fetchQuotationAttachments,
  mapActivitiesToAttachmentRows,
} from '../vendor-quotations/quotationService';
import QuoteAttachment from '../vendor-quotations/QuoteAttachment';
import RequestNudgeComponent from '../../nudge/RequestNudgeComponent';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

const { Option } = Select;

function normalizeFilesToIds(files) {
  if (!Array.isArray(files)) return [];
  return files
    .map((f) => (typeof f === 'string' ? f : f?.id ?? f?._id))
    .filter((id) => id != null && String(id).length > 0);
}

/** Shared due-date rule: required, and at least 2 days before supply date. */
function getDueDateError(dueDate, supplyDate) {
  if (!dueDate) return 'Select due date';
  if (
    supplyDate &&
    dayjs(dueDate).isAfter(dayjs(supplyDate).subtract(2, 'day').endOf('day'))
  ) {
    return 'Due date must be at least 2 days before supply date';
  }
  return null;
}

/** Tags Select: controlled search + open so the dropdown only appears while typing; filterOption hides already-selected values (Ant Design tags mode otherwise lists them and click toggles off). */
function PlaceOfSupplyTagsSelect({ value, onChange, disabled, onAfterChange, ...rest }) {
  const [searchValue, setSearchValue] = useState('');
  const dropdownOpen = searchValue.trim().length > 0;
  const selected = Array.isArray(value) ? value : [];

  return (
    <Select
      mode="tags"
      value={value}
      searchValue={searchValue}
      onSearch={setSearchValue}
      filterOption={(input, option) => {
        const optVal = String(option?.value ?? '');
        if (selected.includes(optVal)) return false;
        return optVal.toLowerCase().includes(String(input || '').toLowerCase());
      }}
      onChange={(v) => {
        onChange?.(v);
        setSearchValue('');
        onAfterChange?.();
      }}
      open={dropdownOpen}
      onDropdownVisibleChange={(visible) => {
        if (!visible) {
          setSearchValue('');
        }
      }}
      menuItemSelectedIcon={null}
      disabled={disabled}
      {...rest}
    />
  );
}

const unitsList = [
  { label: 'units', value: 'units' },
  { label: 'kg', value: 'kg' },
  { label: 'gm', value: 'g' },
  { label: 'ltr', value: 'l' },
  { label: 'ml', value: 'ml' },
  { label: 'mtr', value: 'm' },
  { label: 'ft', value: 'ft' },
  { label: 'inch', value: 'inch' }
];

export default function ModalQuotesComponent({
  isVisible,
  onClose,
  modalData,
  type,
  quoteID,
  setTrigger,
  /** true only when opened via sellers list horizontal "Ask Quotes" (quotes-vendor) */
  showVendorLink = false,
}) {
  const [form] = Form.useForm();
  const [itemsCounter, setItemsCounter] = useState([{ quantity: { unit: 'units' } }]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState('New Quotation Title');
  const [popoverLink, setPopoverLink] = useState(false);
  const [popoverFloatPrice, setPopoverFloatPrice] = useState({});
  const [seeForm, setSeeForm] = useState(null)
  const [loading, setLoading] = useState({ isLoading: false, isFetchingQuote: false });
  const [disableFields, setDisableFields] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  const [quoteState, setQuoteState] = useState({ isEditForm: false });
  const [quoteIdChange, setQuoteIdChange] = useState(false);
  const [id, setId] = useState();
  const [linkquote, setLinkQuote] = useState(false);
  const [attachmentList, setAttachmentList] = useState([]);
  const [attachmentID, setAttachmentID] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [originalDueDates, setOriginalDueDates] = useState([]);
  const [currentQuoteStatus, setCurrentQuoteStatus] = useState(null);
  const [showReadyConfirmModal, setShowReadyConfirmModal] = useState(false);
  const [isMarkedReady, setIsMarkedReady] = useState(false);
  const emojiToolbarRef = useRef(null);
  const linkSwitchOnRef = useRef(false);

  const itemsWatcher = Form.useWatch('items', form);
  const superCategoryWatcher = Form.useWatch('superCategory', form);
  const [options, setOptions] = useState([{ label: 'New', value: '' }]);
  const user = useSelector((state) => state.user);
  const generateNewQuoteId = () => 'QID-' + Math.floor(Math.random() * 100000);

  // Freeze only after Mark as Ready in-session, or read-only dashboard open (seeForm, non-draft)
  const isSubmittedQuote = ['pending', 'active'].includes(currentQuoteStatus);
  const isTerminalQuote = ['completed', 'inactive'].includes(currentQuoteStatus);
  const isFormFrozen =
    isMarkedReady ||
    (type === 'seeForm' && currentQuoteStatus && currentQuoteStatus !== 'draft');
  const showDownload =
    isMarkedReady ||
    (type === 'seeForm' && currentQuoteStatus && currentQuoteStatus !== 'draft');
  const showSubmitButton =
    (isMarkedReady || (type === 'seeForm' && currentQuoteStatus === 'ready')) &&
    !isSubmittedQuote &&
    !isTerminalQuote;
  const isVendorLinkingMode = showVendorLink && switchChecked;
  const canUpdateDueDates =
    showDownload &&
    !!itemsWatcher?.length &&
    itemsWatcher.every((item) => !getDueDateError(item?.dueDate, item?.supplyDate));

  const applyActiveQuoteId = useCallback(
    (quoteId) => {
      setActiveQuoteId(quoteId);
      form.setFieldsValue({ quoteId });
    },
    [form]
  );

  const buildQuotePayload = (restValues, status, fileIds, quoteId) => ({
    ...restValues,
    quoteId,
    status,
    files: fileIds,
  });

  useEffect(() => {
    const DEFAULT_QUOTE_ID = generateNewQuoteId();

    fetch(`${BASE_URL}/quotations/quoteids/${user.org}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    })
      .then(response => response.json())
      .then(data => {
        const newOptions = data.map(item => ({ 
          label: item.quoteId, 
          value: item.quoteId,
          status: item.status 
        }));
        setOptions([{ label: 'New', value: DEFAULT_QUOTE_ID, status: null }, ...newOptions]);
      })
      .catch(error => console.error('Error:', error));
  }, [isVisible, form]);

  // Mark as Ready - saves quote with status 'ready' and shows confirmation
  const onFinish = async (values) => {
    if (!activeQuoteId) {
      showErrorMessage('Quote ID is missing', 3);
      return;
    }
    setLoading({ ...loading, isLoading: true });
    const { files: formFiles, ...restValues } = values;
    const fileIds = normalizeFilesToIds(formFiles);
    const requestData = {
      pid: '',
      quote: buildQuotePayload(restValues, 'ready', fileIds, activeQuoteId),
    };
    try {
      const response = await postData("/quotations", requestData);
      if (response.status == 'success') {
        showSuccessMessage("Quotation marked as ready!", 3);
        let _qid = activeQuoteId;
        if (response.data?.quoteId) {
          _qid = response.data.quoteId;
          applyActiveQuoteId(_qid);
        }
        await refreshQuotationAttachments(_qid);
        setCurrentQuoteStatus('ready');
        setIsMarkedReady(true);
        setSeeForm(true);
        setShowReadyConfirmModal(true);
        // Toggle trigger to refresh dashboard
        if (typeof setTrigger === 'function') setTrigger(prev => !prev);
      } else {
        showErrorMessage(response.message? response.message: "Something went wrong!!", 3);
      }
      setLoading({ ...loading, isLoading: false });
    } catch (error) {
      showErrorMessage("Something went wrong!!", 3);
      setLoading({ ...loading, isLoading: false });
    }
  };

  // Submit quote to vendors (after marking as ready)
  const handleSubmitToVendors = async () => {
    if (!activeQuoteId) {
      showErrorMessage('Quote ID is missing', 3);
      return;
    }
    setLoading({ ...loading, isLoading: true });
    setShowReadyConfirmModal(false);
    
    try {
      const values = form.getFieldsValue(true);
      const { files: formFiles, ...restValues } = values;
      const fileIds = normalizeFilesToIds(formFiles);
      const requestData = {
        pid: _.get(modalData, 'sinkPartnerId', ''),
        quote: buildQuotePayload(restValues, 'pending', fileIds, activeQuoteId),
      };
      
      const response = await postData("/quotations", requestData);
      if (response.status === 'success') {
        showSuccessMessage("Quotation submitted successfully!", 3);
        setCurrentQuoteStatus('pending');
        setSeeForm(true);
        if (typeof setTrigger === 'function') setTrigger(prev => !prev);

        if (showVendorLink && switchChecked) {
          onClose();
        } else if (type === 'newQuote') {
          onClose();
        }
      } else {
        showErrorMessage(response.message || "Failed to submit quotation", 3);
      }
      setLoading({ ...loading, isLoading: false });
    } catch (error) {
      showErrorMessage("Something went wrong!!", 3);
      setLoading({ ...loading, isLoading: false });
    }
  };

  const handleSaveDraft = async () => {
    if (!activeQuoteId) {
      showErrorMessage('Quote ID is missing', 3);
      return;
    }
    const values = form.getFieldsValue(true);
    setLoading({ ...loading, isLoading: true });
    const { files: formFiles, ...restValues } = values;
    const fileIds = normalizeFilesToIds(formFiles);
    const requestData = {
      pid: '',
      quote: buildQuotePayload(restValues, 'draft', fileIds, activeQuoteId),
    };
    try {
      const response = await postData("/quotations", requestData);
      if (response.status == 'success') {
        showSuccessMessage("Saved as draft successfully!!", 3);
        let _qid = activeQuoteId;
        if (response.data?.quoteId) {
          applyActiveQuoteId(_qid);
          _qid = response.data.quoteId;
        }
        await refreshQuotationAttachments(_qid);
        if(type === 'seeFormDraft' || type === 'newQuote'){
          if (typeof setTrigger === 'function') setTrigger(prev => !prev);
          onClose();
        }
      } else {
        showErrorMessage(response.message? response.message: "Something went wrong!!", 3);
      }
      setLoading({ ...loading, isLoading: false });
    } catch (error) {
      showErrorMessage("Something went wrong!!", 3);
      setLoading({ ...loading, isLoading: false });
    }
  };

  const handleUpdateDueDates = async () => {
    if (!activeQuoteId) {
      showErrorMessage('Quote ID is missing', 3);
      return;
    }
    const values = form.getFieldsValue(true);
    const formItems = values.items || [];

    const firstError = formItems
      .map((item) => getDueDateError(item?.dueDate, item?.supplyDate))
      .find(Boolean);
    if (firstError) {
      showErrorMessage(firstError, 3);
      return;
    }

    setLoading({ ...loading, isLoading: true });
    const { files: formFiles, ...restValues } = values;
    const fileIds = normalizeFilesToIds(formFiles);

    // Keep existing status so POST upsert updates due dates without re-triggering ready/submit flows.
    const requestData = {
      pid: '',
      quote: buildQuotePayload(
        restValues,
        currentQuoteStatus || 'ready',
        fileIds,
        activeQuoteId
      ),
    };

    try {
      const response = await postData('/quotations', requestData);
      if (response?.status === 'success') {
        showSuccessMessage('Due dates updated successfully!!', 3);
        setOriginalDueDates(formItems.map((item) => (item.dueDate ? dayjs(item.dueDate) : null)));
        if (typeof setTrigger === 'function') setTrigger((prev) => !prev);
      } else {
        showErrorMessage(response?.message || 'Something went wrong!!', 3);
      }
      setLoading({ ...loading, isLoading: false });
    } catch (error) {
      showErrorMessage('Something went wrong!!', 3);
      setLoading({ ...loading, isLoading: false });
    }
  };

  const handleAddNewItem = (copyLast = false) => {
    const allValues = form.getFieldsValue();
    const currentItems = allValues.items || [];
    
    let newItemCounter = { quantity: { unit: 'units' } };
    let newFormItem = { quantity: { unit: 'units' } };
    
    if (copyLast && currentItems.length > 0) {
      const lastItem = currentItems[currentItems.length - 1];
      newFormItem = { ...lastItem };
      newItemCounter = { ...itemsCounter[itemsCounter.length - 1] };
    }
    
    setItemsCounter([...itemsCounter, newItemCounter]);
    
    form.setFieldsValue({
      ...allValues,
      items: [...currentItems, newFormItem]
    });
  };

  const handleDeleteItem = (indexToDelete) => {
    const newItems = [...itemsCounter];
    newItems.splice(indexToDelete, 1);
    const allValues = form.getFieldsValue();
    const updatedFormValues = {
      ...allValues,
      items: allValues.items.filter((_, index) => index !== indexToDelete)
    };
  
    form.setFieldsValue(updatedFormValues);
    setItemsCounter(newItems);
  };

  const handleCopyQuoteId = (quoteId) => {
    if (quoteId) {
      navigator.clipboard.writeText(quoteId);
      message.success('Copied to clipboard');
    } else {
      message.warning('Please select a quote');
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    handleFieldChange();
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
  };
 const handleWarning = () => {
    showErrorMessage('Please Select existing quoteId');
 }
  const handleSwitchChange = (checked) => {
    linkSwitchOnRef.current = checked;
    if (checked) {
      handleSwitchChecked(checked);
    } else {
      const newQuoteId = generateNewQuoteId();
      applyActiveQuoteId(newQuoteId);
      setAttachmentList([]);
      form.setFieldsValue({ files: [] });
    }
    setQuoteState({ ...quoteState, isEditForm: true });
    setQuoteIdChange(false);
    setSwitchChecked(checked);
    setDisableFields(checked);
  };

  const handleSwitchChecked = async (checked) => {
    if (checked) {
      handleSelectChange(id, 'switch');
    }
  };

  const handleLink = () => {
    setPopoverLink(!popoverLink);
  };

  const handleFloatPricePopover = (index) => {
    setPopoverFloatPrice(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const refreshQuotationAttachments = useCallback(
    async (key) => {
      if (!key) {
        setAttachmentList([]);
        return;
      }
      try {
        const raw = await fetchQuotationAttachments(key);
        const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        const rows = mapActivitiesToAttachmentRows(arr);
        setAttachmentList(rows);
        form.setFieldsValue({ files: rows.map((r) => r._id) });
      } catch (e) {
        console.error('fetchQuotationAttachments', e);
      }
    },
    [form]
  );

  const handleSelectChange = async (value, selectMode) => {
    try {
      setId(value)
      if (value === options[0].value) {
        form.resetFields();
        setItemsCounter([{ quantity: { unit: 'units' } }]);
        setTitle('New Quotation Title');
        setDisableFields(false);
        setSwitchChecked(false);
        setLinkQuote(false);
        setAttachmentList([]);
        setCurrentQuoteStatus(null);
        applyActiveQuoteId(value);
      } else {
        setAttachmentList([]);
        setLoading((prev) => ({ ...prev, isFetchingQuote: true }));
        const response = await fetchQuoteWithQuoteId(value);
        if (!response) {
          message.error('Failed to fetch quote');
          setLoading((prev) => ({ ...prev, isFetchingQuote: false }));
          return;
        }
        const transformedResponse = {
          quoteId: response.quoteId,
          title: response.title,
          emails: response?.emailRecipient,
          description: response.description,
          items: (response.items || []).map(item => ({
            item: item.name,
            quantity: {
              unit: item.unit,
              value: item.quantity
            },
            dueDate: item.dueDate ? dayjs(item.dueDate): "",
            minBudget: item.budget?.min,
            maxBudget: item.budget?.max,
            placeOfSupply: item.placeOfSupply,
            supplyDate: item.supplyDate ? dayjs(item.supplyDate): "",
            skuId: item.skuId,
            openPrice: item.openPrice || false,
            subCategory: item.subCategory
          })),
          templateURL: response?.templateURL,
          superCategory: response.superCategory,
          shipping: {
            address: response?.shipping?.address,
            location: response?.shipping?.location,
          },
          billing: {
            address: response?.billing?.address,
          },
        };
        setItemsCounter(transformedResponse.items?.length ? transformedResponse.items : [{ quantity: { unit: 'units' } }]);
        setTitle(response.title || 'New Quotation Title');
        setQuoteState({ ...quoteState, isEditForm: true });
        setLinkQuote(true);
        if (selectMode === 'seeForm' || selectMode === 'seeFormDraft') {
          setCurrentQuoteStatus(response.status || null);
        } else if (selectMode === 'select') {
          setIsMarkedReady(false);
        }

        // Store original dueDates for read-only submitted view (dashboard seeForm)
        if (type === 'seeForm') {
          const dueDates = (response.items || []).map(item => 
            item.dueDate ? dayjs(item.dueDate) : null
          );
          setOriginalDueDates(dueDates);
        } else {
          setOriginalDueDates([]);
        }

        const preserveQuoteId =
          selectMode === 'switch' || selectMode === 'seeForm' || selectMode === 'seeFormDraft';

        if (preserveQuoteId) {
          const stillLinked = selectMode !== 'switch' || linkSwitchOnRef.current;
          if (stillLinked) {
            applyActiveQuoteId(response.quoteId);
            form.setFieldsValue(transformedResponse);
            await refreshQuotationAttachments(response.quoteId);
          } else {
            const newQuoteId = generateNewQuoteId();
            applyActiveQuoteId(newQuoteId);
            form.setFieldsValue({ ...transformedResponse, quoteId: newQuoteId, files: [] });
            setAttachmentList([]);
          }
        } else {
          const newQuoteId = generateNewQuoteId();
          applyActiveQuoteId(newQuoteId);
          form.setFieldsValue({ ...transformedResponse, quoteId: newQuoteId, files: [] });
          setAttachmentList([]);
        }
        setLoading((prev) => ({ ...prev, isFetchingQuote: false }));
      }
    } catch (error) {
      message.error('Failed to fetch data from API');
      setLoading((prev) => ({ ...prev, isFetchingQuote: false }));
    }
  };

  const handleFieldChange = () => {
    if (!quoteIdChange && quoteState.isEditForm && type !== 'seeFormDraft' && !switchChecked) {
      applyActiveQuoteId(generateNewQuoteId());
      setQuoteIdChange(true);
    }
  };

  const handleDelete = (file) => {
    setAttachmentID(file._id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
  };

  const handleDeleteAttachment = (id) => {
    fetch(`${BASE_URL}/activities/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => {
        if (response.status === 200) {
          setAttachmentList((prev) => prev.filter((f) => f._id !== id));
          const curIds = form.getFieldValue('files') || [];
          form.setFieldsValue({ files: curIds.filter((fid) => fid !== id) });
          showSuccessMessage('File deleted successfully');
          setDeleteModal(false);
        } else {
          showErrorMessage('File deletion failed');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const hasQuoteIdFromDropdown = Boolean(activeQuoteId);
  const attachmentClipDisabled = isFormFrozen || !hasQuoteIdFromDropdown;
  const attachmentClipTooltip = isFormFrozen
    ? undefined
    : !hasQuoteIdFromDropdown
      ? 'Select New or an existing quote from Auto Form-Fill before attaching files.'
      : undefined;

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      fileList,
      showUploadList: false,
      openFileDialogOnClick: true,
      disabled: attachmentClipDisabled,
      customRequest: async ({ file, onSuccess, onError }) => {
        const qid = activeQuoteId;
        if (!qid) {
          showErrorMessage('Select a quote ID before attaching files');
          onError(new Error('No quote key'));
          return;
        }
        const formdata = new FormData();
        formdata.append('file', file);
        formdata.append('key', qid);
        try {
          const res = await uploadFile('/uploads/aws/quotation', formdata);
          if (res.success) {
            onSuccess(res, file);
          } else {
            showErrorMessage(res.message || 'File upload failed');
            onError(new Error(res.message || 'Upload failed'));
          }
        } catch (e) {
          showErrorMessage('File upload failed');
          onError(e);
        }
      },
      onChange: (info) => {
        const next = [...info.fileList];
        setFileList(next);
        if (info.file.status === 'done') {
          setFileList([]);
          showSuccessMessage('File uploaded successfully');
          const res = info.file.response;
          const newId = res?.data?.id ?? res?.data?._id;
          if (newId) {
            const curIds = form.getFieldValue('files') || [];
            form.setFieldsValue({ files: [...curIds, newId] });
          }
          void refreshQuotationAttachments(activeQuoteId);
        } else if (info.file.status === 'error') {
          showErrorMessage('File upload failed');
        }
      },
    }),
    [fileList, attachmentClipDisabled, activeQuoteId, refreshQuotationAttachments]
  );

  useClickOutSide(emojiToolbarRef, () => {
    setShowEmojiPicker(false);
  });

  const handleDescriptionEmoji = (emojiData) => {
    const ch = emojiData?.emoji ?? '';
    if (!ch) return;
    const current = form.getFieldValue('description') ?? '';
    form.setFieldsValue({ description: `${current}${ch}` });
    setShowEmojiPicker(false);
  };

  useEffect(() => {
    if (isVisible) {
      form.resetFields();
      setItemsCounter([{ quantity: { unit: 'units' } }]);
      setIsEditingTitle(false);
      setTitle('New Quotation Title');
      setDisableFields(false);
      setSwitchChecked(false);
      setQuoteState({isEditForm: false });
      setQuoteIdChange(false)
      setActiveQuoteId(null);
      setLinkQuote(false);
      setId(null)
      setAttachmentList([]);
      setDeleteModal(false);
      setAttachmentID(null);
      setFileList([]);
      setShowEmojiPicker(false);
      setOriginalDueDates([]);
      setCurrentQuoteStatus(null);
      setShowReadyConfirmModal(false);
      setIsMarkedReady(false);
      linkSwitchOnRef.current = false;
    }
  }, [isVisible, form]);

  useEffect(() => {
    if (!isVisible) return;
    if (type === 'seeForm') {
      setSeeForm(true);
    } else if (type === 'seeFormDraft') {
      setSeeForm(false);
    }
    if (type && type !== 'newQuote' && quoteID) {
      handleSelectChange(quoteID, type);
    }
  }, [isVisible, type, quoteID]);

  const initialValues = {
    title: title,
    quoteId: undefined,
    emails: [],
    description: '',
    templateURL: '',
    items: [{ quantity: { unit: 'units' } }],
    files: [],
  };

  const getDownloadData = () => {
    const formData = form.getFieldsValue(true);
    const headers = [
      'Sr.',
      'Item',
      'Quantity',
      'Unit',
      'Due Date',
      'Min Budget',
      'Max Budget',
      'Place of Supply',
      'Supply Date'
    ];

    const rows = (formData.items || []).map((item, index) => [
      index + 1,
      item.item || '',
      item.quantity?.value || '',
      item.quantity?.unit || '',
      item.dueDate ? dayjs(item.dueDate).format('DD/MM/YYYY') : '',
      item.openPrice ? 'Best Price' : (item.minBudget || ''),
      item.openPrice ? 'Best Price' : (item.maxBudget || ''),
      Array.isArray(item.placeOfSupply) ? item.placeOfSupply.join(', ') : (item.placeOfSupply || ''),
      item.supplyDate ? dayjs(item.supplyDate).format('DD/MM/YYYY') : ''
    ]);

    return { formData, headers, rows };
  };

  const handleDownloadCSV = () => {
    const { formData, headers, rows } = getDownloadData();

    const metaRows = [
      ['Quote ID', activeQuoteId],
      ['Title', formData.title || ''],
      ['Category', formData.superCategory || ''],
      ['Description', formData.description || ''],
      ['Shipping Address', formData.shipping?.address || ''],
      ['Billing Address', formData.billing?.address || ''],
      [''],
    ];

    const csvContent = [
      ...metaRows.map(row => row.join(',')),
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `quotation_${activeQuoteId}.csv`;
    saveAs(blob, fileName);
  };

  const handleDownloadPDF = () => {
    const { formData, headers, rows } = getDownloadData();
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text('Quotation Request', 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Quote ID: ${activeQuoteId}`, 14, 25);
    doc.text(`Title: ${formData.title || '-'}`, 14, 31);
    doc.text(`Category: ${formData.superCategory || '-'}`, 14, 37);
    doc.text(`Shipping: ${formData.shipping?.address || '-'}`, 14, 43);
    doc.text(`Billing: ${formData.billing?.address || '-'}`, 14, 49);

    if (formData.description) {
      doc.text(`Description: ${formData.description}`, 14, 55);
    }

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: formData.description ? 62 : 56,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [72, 0, 255] },
    });

    doc.save(`quotation_${activeQuoteId}.pdf`);
  };

  const downloadMenuItems = [
    { key: 'csv', label: 'Download as CSV', onClick: handleDownloadCSV },
    { key: 'pdf', label: 'Download as PDF', onClick: handleDownloadPDF },
  ];

  return (
    <>
    <Modal
      open={isVisible}
      onCancel={onClose}
      title="Request for Quote(RFQ)"
      footer={null}
      destroyOnClose
      className='Quotes_modal'>
      <Form form={form} onFinish={onFinish} initialValues={initialValues}>
          <Form.Item name="files" hidden preserve>
            <Select mode="tags" open={false} suffixIcon={null} style={{ width: 0, maxWidth: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
          </Form.Item>
          <div className='Quotes_container'>
            <div style={{width: '100%'}}>
              <span style={{fontSize: '16px'}}>Quote</span>
              <div className='Quotes_firstContainer'>
              <div>
                <Form.Item name="quoteId" label="Auto Form-Fill:" rules={[{ required: true, message: 'Select a Quote Id' }]}>
                  <Select placeholder="Select" onChange={(value) => handleSelectChange(value, 'select')} disabled={switchChecked || isFormFrozen}>
                    {options.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="title" rules={[{ required: true, whitespace: true, message: 'Please provide a title' }]}>
                  {isEditingTitle && !switchChecked && !isFormFrozen ? (
                    <div className='ProfileView_subTitleEditing'>
                      <Input onChange={handleTitleChange} value={title} maxLength={45} placeholder="Enter Quotation Title" autoFocus />
                      {title.trim() !== '' ? (
                        <CheckOutlined onClick={handleTitleSave} />
                      ) : (
                        <CheckOutlined style={{ cursor: 'not-allowed', color: 'rgba(0, 0, 0, 0.25)' }} />
                      )}
                    </div>
                  ) : (
                    <span>
                      {title}
                      {!isFormFrozen && !switchChecked && <EditOutlined onClick={handleTitleEdit}/>}
                    </span>
                  )}
                </Form.Item>
              </div>
              <div>
                <span className='quoteIDCopy'>Quote Id: <b>{activeQuoteId || '######'}</b> <CopyOutlined onClick={() => handleCopyQuoteId(activeQuoteId)} /></span>
              {showVendorLink && !isFormFrozen && type !== 'seeFormDraft' && (
                <div className='Quotes_link'>
                    <span>Link
                      <Popover
                        content={
                          <div className='ListComponent_tick'>
                            <InfoCircleOutlined />
                            <div className='ListComponent_tickDetail'>This will be linked to the existing quotation request <Button>Know more</Button></div>
                          </div>
                        }
                        trigger="click"
                        placement="bottomRight"
                        open={popoverLink}
                        onOpenChange={handleLink}>
                        <QuestionCircleOutlined />
                      </Popover>
                    </span>
                    <Switch size="small" onChange={linkquote? handleSwitchChange: handleWarning} checked={switchChecked}/>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='Quotes_secondContainer'>

            {/* <div className='Quotes_secondSubContainer'>
              <span>Mentions</span>
              <Form.Item name="emails" rules={[{ type: 'array', required: false, message: '' }]}>
                <Select mode="tags" placeholder="Type cc emails here..." autoSize={{ minRows: 1, maxRows: 6 }} disabled={switchChecked || isFormFrozen} />
              </Form.Item>
            </div> */}
            <div className='Quotes_secondSubContainer'>
              <p> <ShoppingOutlined /> Category</p>
              <Form.Item
                name="superCategory"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select 
                  showSearch
                  placeholder="Select or type category" 
                  disabled={switchChecked || isFormFrozen}
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={() => {
                    // Clear all subcategory selections when super category changes
                    const currentItems = form.getFieldValue('items') || [];
                    const updatedItems = currentItems.map(item => ({ ...item, subCategory: undefined }));
                    form.setFieldsValue({ items: updatedItems });
                  }}
                >
                  {SUPER_CATEGORY_LIST.map(cat => (
                    <Option key={cat.value} value={cat.value}>{cat.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className='Quotes_secondSubContainer'>
              <p>Description</p>
              <Form.Item name="description" rules={[{ required: true, whitespace: true, message: 'Please provide a description' }]}>
                <Input.TextArea placeholder="Type description here about the items in detail..." rows={3} disabled={switchChecked || isFormFrozen} style={{ resize: 'vertical' }} />
              </Form.Item>
              <div className="Quotation_inputFooter" style={{ marginTop: 8 }}>
                <div className="Quotation_inputFeature">
                  <i style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <Tooltip title={attachmentClipTooltip}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          cursor: attachmentClipDisabled ? 'not-allowed' : 'pointer',
                          opacity: attachmentClipDisabled ? 0.45 : 1,
                        }}
                      >
                        <Upload {...uploadProps}>
                          <span
                            role="button"
                            tabIndex={attachmentClipDisabled ? -1 : 0}
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                          >
                            <PaperClipOutlined style={{ fontSize: 18 }} />
                          </span>
                        </Upload>
                      </span>
                    </Tooltip>
                    <hr style={{ height: 16, margin: 0, border: 'none', borderLeft: '1px solid #d9d9d9' }} />
                    <span ref={emojiToolbarRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                      {showEmojiPicker && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            marginBottom: 8,
                            zIndex: 10,
                          }}
                        >
                          <Picker onEmojiClick={handleDescriptionEmoji} />
                        </div>
                      )}
                      <SmileOutlined
                        onClick={() => {
                          if (!isFormFrozen) setShowEmojiPicker((prev) => !prev);
                        }}
                        style={{
                          fontSize: 18,
                          cursor: isFormFrozen ? 'not-allowed' : 'pointer',
                          opacity: isFormFrozen ? 0.45 : 1,
                        }}
                      />
                    </span>
                  </i>
                </div>
              </div>
            </div>
            <QuoteAttachment
              files={attachmentList}
              handleDelete={handleDelete}
              handleFiles={() => {
                void refreshQuotationAttachments(activeQuoteId);
              }}
              fileCounts={{}}
              type2="newQuote"
            />
            <div className='Quotes_items'>
              {itemsCounter.map((item, index) => (
                <div className='Quotes_itemsData' key={index}>
                  <div className='Quotes_itemTitle'>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      Add Item {index + 1}
                      {item.skuId && (
                        <Tag style={{ marginLeft: '8px', color: '#4800FF', border: 'none', background: '#EDE7FF' }}>
                          <TagOutlined style={{ marginRight: '4px' }} />{item.skuId}
                        </Tag>
                      )}
                    </span>
                    {itemsCounter.length > 1 && !switchChecked && !isFormFrozen && (
                      <DeleteFilled onClick={() => handleDeleteItem(index)} className="deleteIcon" />
                    )}
                  </div>
                  <Typography className='Quotes_typography' onChange={handleFieldChange}>
                    <div>
                      <div>
                        <p>Name</p>
                        <Form.Item name={['items', index, 'item']} style={{ width: '14vw' }} rules={[{ required: true, whitespace: true, message: 'Provide name' }]}>
                          <Input placeholder='Type an item name...' disabled={switchChecked || isFormFrozen} />
                        </Form.Item>
                      </div>
                      <div>
                        <p>Quantity</p>
                        <Form.Item name={['items', index, 'quantity']} initialValue={{ value: '', unit: 'units' }} rules={[{ required: true, message: 'Provide quantity' }]}>
                          <Input.Group compact>
                            <Form.Item name={['items', index, 'quantity', 'value']} noStyle rules={[{ type: 'number', required: true, message: 'Provide value' }]}>
                              <InputNumber style={{ borderRadius: '3px 0 0 3px' }} placeholder='Quantity' disabled={switchChecked || isFormFrozen}/>
                            </Form.Item>
                            <Form.Item name={['items', index, 'quantity', 'unit']} noStyle rules={[{ required: true, message: 'Select unit' }]}>
                              <Select className='Quotes_units' disabled={switchChecked || isFormFrozen} onChange={handleFieldChange}>
                                {unitsList.map(unit => (
                                  <Option key={unit.label} value={unit.label}>
                                    {unit.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Input.Group>
                        </Form.Item>
                      </div>
                      <div>
                        <p>Budget</p>
                        <Form.Item name={['items', index, 'minBudget']} rules={[{ type: 'number', required: !(itemsWatcher && itemsWatcher[index]?.openPrice), message: 'Provide minimum budget' }]}>
                          <InputNumber style={{ width: '100%' }} min={1} max={10000000} placeholder='Minimum' prefix={<span>&#x20B9;</span>} disabled={switchChecked || isFormFrozen || (itemsWatcher && itemsWatcher[index]?.openPrice)}/>
                        </Form.Item>
                      </div>
                      <div className='Quotes_hr'>&ndash;</div>
                      <div>
                        <Form.Item 
                          name={['items', index, 'maxBudget']} 
                          dependencies={[['items', index, 'minBudget']]}
                          rules={[
                            { type: 'number', required: !(itemsWatcher && itemsWatcher[index]?.openPrice), message: 'Provide maximum budget' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const minBudget = getFieldValue(['items', index, 'minBudget']);
                                if (!value || !minBudget || value >= minBudget) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Max budget must be >= min budget'));
                              },
                            }),
                          ]}
                        >
                          <InputNumber style={{ width: '100%' }} min={1} max={10000000} placeholder='Maximum' prefix={<span>&#x20B9;</span>} disabled={switchChecked || isFormFrozen || (itemsWatcher && itemsWatcher[index]?.openPrice)}/>
                        </Form.Item>
                      </div>
                      <div>
                        <Form.Item 
                          label={
                            <span>
                              Float Price
                              <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                <Popover
                                  content={
                                    <div className='ListComponent_tick'>
                                      <InfoCircleOutlined />
                                      <div className='ListComponent_tickDetail'>Enable this to allow vendors to propose their own pricing <Button>Know more</Button></div>
                                    </div>
                                  }
                                  trigger="click"
                                  placement="bottomRight"
                                  open={popoverFloatPrice[index]}
                                  onOpenChange={() => handleFloatPricePopover(index)}>
                                  <QuestionCircleOutlined style={{ marginLeft: '4px', cursor: 'pointer', color: '#4800FF' }} />
                                </Popover>
                              </span>
                            </span>
                          }
                          name={['items', index, 'openPrice']}
                          valuePropName="checked"
                          rules={[{ type: 'boolean', message: '' }]}>
                          <Switch 
                            style={{ width: '100%' }} 
                            checkedChildren="Yes" 
                            unCheckedChildren="No" 
                            disabled={switchChecked || isFormFrozen}
                            onChange={(checked) => {
                              if (checked) {
                                const currentItems = form.getFieldValue('items') || [];
                                const updatedItems = [...currentItems];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  minBudget: undefined,
                                  maxBudget: undefined,
                                };
                                form.setFieldsValue({ items: updatedItems });
                              }
                            }}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div style={{ alignItems: 'flex-start', width: '100%' }}>
                    <div>
                        <p>Supply Date</p>
                        <Form.Item name={['items', index, 'supplyDate']} rules={[{ required: true, message: 'Select supply date' }]}>
                          <DatePicker 
                            placeholder='Select a date' 
                            disabled={switchChecked || isFormFrozen}
                            disabledDate={(current) => {
                              if (!current) return false;
                              const today = dayjs().startOf('day');
                              return current.isBefore(today);
                            }}
                          />
                        </Form.Item>
                      </div>
                      <div>
                        <p>Due Date</p>
                        <Form.Item 
                          name={['items', index, 'dueDate']} 
                          dependencies={[['items', index, 'supplyDate']]}
                          rules={[
                            { required: true, message: 'Select due date' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const error = getDueDateError(
                                  value,
                                  getFieldValue(['items', index, 'supplyDate'])
                                );
                                if (!value || !error || error === 'Select due date') {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error(error));
                              },
                            }),
                          ]}
                        >
                          <DatePicker 
                            placeholder='Select a date' 
                            disabled={switchChecked}
                            disabledDate={(current) => {
                              if (!current) return false;
                              if (current.isBefore(dayjs().startOf('day'))) return true;
                              if (
                                seeForm &&
                                originalDueDates[index] &&
                                current.isBefore(dayjs(originalDueDates[index]).startOf('day'))
                              ) {
                                return true;
                              }
                              return !!getDueDateError(current, itemsWatcher?.[index]?.supplyDate);
                            }}
                          />
                        </Form.Item>
                      </div>
                    <div style={{ width: '25%' }}>
                      <span>Sub Category</span>
                      <Form.Item name={['items', index, 'subCategory']}>
                        <Select 
                          placeholder={superCategoryWatcher ? "Select sub category" : "Select category first"} 
                          disabled={switchChecked || isFormFrozen || !superCategoryWatcher}
                        >
                          {getSubCategoriesForSuperCategory(superCategoryWatcher).map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                    <div style={{ width: '25%' }}>
                        <p>Place of supply</p>
                        <Form.Item name={['items', index, 'placeOfSupply']} rules={[{ type: 'array', required: true, message: 'At least one city name is required' }]}>
                          <PlaceOfSupplyTagsSelect
                            placeholder="Type city names..."
                            disabled={switchChecked || isFormFrozen}
                            onAfterChange={handleFieldChange}
                          />
                        </Form.Item>
                    </div>
                    </div>
                  </Typography>
                </div>
              ))}
              {!isFormFrozen && !switchChecked && (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div className='Quotes_addNew' onClick={() => handleAddNewItem(false)}>
                    <PlusSquareOutlined /> Add New
                  </div>
                  <div className='Quotes_addNew' onClick={() => handleAddNewItem(true)}>
                    <DiffOutlined /> Duplicate Last
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <div className='Quotes_secondSubContainer' style={{ flex: 1 }}>
                <p><TruckOutlined /> Shipping Address</p>
                <Form.Item name={['shipping', 'address']} rules={[{ required: true, message: 'Provide delivery address' }]}>
                  <Input.TextArea placeholder="Type shipping address here..." rows={2} disabled={switchChecked || isFormFrozen} />
                </Form.Item>
              </div>
              <div className='Quotes_secondSubContainer' style={{ flex: 1 }}>
                <span><HomeOutlined /> Billing Address</span>
                <Form.Item name={['billing', 'address']}>
                  <Input.TextArea placeholder="Type billing address here..." rows={2} disabled={switchChecked || isFormFrozen} />
                </Form.Item>
              </div>
            </div>
            <div className='Quotes_secondSubContainer'>
              <span> <EnvironmentOutlined />Shipping Location</span> 
              <Form.Item name={['shipping', 'location']}>
                <Input placeholder="Mention the google map location here..." disabled={switchChecked || isFormFrozen} />
              </Form.Item>
            </div>
            {/* <div className='Quotes_secondSubContainer'>
              <span> <LinkOutlined />Template URL</span>
              <Form.Item name="templateURL">
                <Input placeholder="Paste your own custom quote template doc link here" disabled={switchChecked || isFormFrozen} />
              </Form.Item>
            </div> */}
          </div>
        </div>
        <div className="Quotes_footer">
          {!isFormFrozen && !switchChecked && (
            <Form.Item>
              <Button
                type="default"
                style={{ backgroundColor: 'transparent', border: '1px solid transparent', color: '#4800FF' }}
                onClick={handleSaveDraft}
              >
                Save as Draft
              </Button>
            </Form.Item>
          )}
          <Form.Item>
            {showDownload && showSubmitButton ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']}>
                  <Button type="default">
                    <DownloadOutlined /> Download <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type="primary" onClick={handleUpdateDueDates} disabled={!canUpdateDueDates}>
                  Update
                </Button>
                <Button type="primary" onClick={handleSubmitToVendors}>Submit</Button>
              </div>
            ) : showDownload ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']}>
                  <Button type="default">
                    <DownloadOutlined /> Download <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type="primary" onClick={handleUpdateDueDates} disabled={!canUpdateDueDates}>
                  Update
                </Button>
              </div>
            ) : isVendorLinkingMode ? (
              <Button
                type="primary"
                onClick={handleSubmitToVendors}
                disabled={!linkquote || !activeQuoteId}
              >
                Submit
              </Button>
            ) : (
              <Button type="primary" htmlType="submit">Mark as Ready</Button>
            )}
          </Form.Item>
        </div>
      </Form>
      {loading.isLoading && showLoader('Sending')}
      {loading.isFetchingQuote && showLoader('Fetching')}
    </Modal>
    
    <RequestNudgeComponent
      type="quotationReady"
      isVisible={showReadyConfirmModal}
      onClose={() => setShowReadyConfirmModal(false)}
      handleNudgeSubmit={handleSubmitToVendors}
    />
    
    <RequestNudgeComponent
      type="deleteAttachment"
      data={attachmentID}
      isVisible={deleteModal}
      onClose={closeDeleteModal}
      handleNudgeSubmit={handleDeleteAttachment}
    />
    </>
  );
}
